// The blueprint mode of a tile's interior (PRD sections 6, 10 and 19.7, D8): what the player is
// building is a ghost until it is confirmed. The session holds the chosen tool and the ghost, and
// turns them into H1 commands: `plan` says whether the ghost fits (and why not), `execute` builds it,
// and the command log gives undo and redo. No game rule lives here; it only asks the simulation.
import { beltExitHint } from '../sim/placement';
import { plan, type Command, type Outcome } from '../sim/commands';
import { footprint, keyOf, rotatedSize, rotateSide, sideTowards, type Cell, type Rotation, type Side } from '../sim/geometry';
import type { Simulation } from '../sim/simulation';
import { occupancy, type MachineState } from '../sim/world';
import { extendPath } from './belt-path';

export type Tool =
  | { readonly kind: 'belt' }
  | { readonly kind: 'machine'; readonly machine: string }
  /** Picking up a machine that is already built, to put it down somewhere else (machine detail, «Mover»). */
  | { readonly kind: 'move'; readonly machine: string; readonly from: Cell }
  | { readonly kind: 'remove' };

export type Draft =
  | {
      readonly kind: 'belt';
      readonly path: readonly Cell[];
      /** Side the last cell hands its material on through. */
      readonly exit: Side;
    }
  | { readonly kind: 'machine'; readonly machine: string; readonly origin: Cell; readonly rotation: Rotation };

export interface Ghost {
  readonly draft: Draft;
  readonly command: Command;
  /** Every cell the ghost covers. */
  readonly cells: readonly Cell[];
  readonly fits: boolean;
  /** Why it does not fit, in the simulation's words. */
  readonly reason?: string;
  /** False while a machine being moved still sits where it was: there is nothing to confirm yet. */
  readonly changed: boolean;
}

/** A point on the interior in cell units: (0, 0) is the outer corner of the first cell. */
export interface CellPoint {
  readonly x: number;
  readonly y: number;
}

const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

export class BuildSession {
  private toolValue: Tool | undefined;
  private draftValue: Draft | undefined;
  /** The drag under way, with the ghost from before it, restored if it turns into a two-finger gesture. */
  private drag: { readonly before: Draft | undefined } | undefined;
  /** Rotation given to the next machine: the last one used. */
  private machineRotation: Rotation = 0;
  /** Way a single belt cell points when nothing around says otherwise: the last one used. */
  private beltSide: Side = 'E';

  constructor(
    private readonly sim: Simulation,
    readonly tile: string,
  ) {}

  get tool(): Tool | undefined {
    return this.toolValue;
  }

  get draft(): Draft | undefined {
    return this.draftValue;
  }

  /** Interior size in cells. */
  get size(): Cell {
    return this.sim.world.tiles.get(this.tile)?.interior?.size ?? [0, 0];
  }

  /** Picks a tool from the build bar (or none, to look around); any ghost is dropped. */
  selectTool(tool: Tool | undefined): void {
    this.toolValue = tool;
    this.draftValue = undefined;
    this.drag = undefined;
  }

  /** Picks up the machine covering `cell` as a ghost, ready to be moved. Returns false if there is none. */
  startMove(cell: Cell): boolean {
    const machine = this.machineAt(cell);
    if (!machine) return false;
    this.toolValue = { kind: 'move', machine: machine.def.id, from: machine.origin };
    this.draftValue = { kind: 'machine', machine: machine.def.id, origin: machine.origin, rotation: machine.rotation };
    return true;
  }

  // ------------------------------------------------------------------ belts ---

  /** The finger lands on `cell` with the belt tool: a new ghost belt starts there. */
  beginBelt(cell: Cell): void {
    if (this.toolValue?.kind !== 'belt') return;
    this.drag = { before: this.draftValue };
    this.setPath([cell]);
  }

  /** The finger moves over `cell`: the ghost belt follows it. */
  extendBelt(cell: Cell): void {
    const draft = this.draftValue;
    if (!this.drag || this.toolValue?.kind !== 'belt' || draft?.kind !== 'belt') return;
    const path = extendPath(draft.path, cell, this.size);
    if (path.length !== draft.path.length || path.some((c, i) => keyOf(c) !== keyOf(draft.path[i] ?? [-1, -1]))) this.setPath(path);
  }

  /** A second finger turned the drag into a camera gesture: put back the ghost from before. */
  cancelDrag(): void {
    if (this.drag) this.draftValue = this.drag.before;
    this.drag = undefined;
  }

  /** The finger lifts: the ghost stays as drawn. */
  endDrag(): void {
    this.drag = undefined;
  }

  private setPath(path: readonly Cell[]): void {
    const hint = beltExitHint(this.sim.world, this.tile, path);
    this.draftValue = { kind: 'belt', path, exit: hint ?? this.beltSide };
  }

  // --------------------------------------------------------------- machines ---

  /** The finger lands with a machine tool: the ghost jumps under it, and follows it while it drags. */
  beginMachine(point: CellPoint): void {
    this.drag = { before: this.draftValue };
    this.placeMachineAt(point);
  }

  /** The finger drags the machine ghost: its footprint is centred as near `point` as the grid allows. */
  placeMachineAt(point: CellPoint): void {
    const tool = this.toolValue;
    if (!this.drag || (tool?.kind !== 'machine' && tool?.kind !== 'move')) return;
    const def = this.sim.data.machines.get(tool.machine);
    if (!def) return;
    const draft = this.draftValue;
    const rotation = draft?.kind === 'machine' ? draft.rotation : this.machineRotation;
    const [w, h] = rotatedSize(def.size, rotation);
    const [cols, rows] = this.size;
    const origin: Cell = [clamp(Math.round(point.x - w / 2), 0, Math.max(0, cols - w)), clamp(Math.round(point.y - h / 2), 0, Math.max(0, rows - h))];
    this.draftValue = { kind: 'machine', machine: tool.machine, origin, rotation };
  }

  // ----------------------------------------------------------------- common ---

  /** «Girar»: a machine ghost turns a quarter clockwise; a belt ghost turns the way its last cell points. */
  rotate(): void {
    const draft = this.draftValue;
    if (!draft) return;
    if (draft.kind === 'machine') {
      const rotation = ((draft.rotation + 1) % 4) as Rotation;
      this.draftValue = { ...draft, rotation };
      return;
    }
    const last = draft.path[draft.path.length - 1];
    const prev = draft.path[draft.path.length - 2];
    const back = last && prev ? sideTowards(last, prev) : undefined;
    let exit = rotateSide(draft.exit, 1);
    if (exit === back) exit = rotateSide(exit, 1);
    this.draftValue = { ...draft, exit };
  }

  /** The ghost to draw, with whether it fits. */
  ghost(): Ghost | undefined {
    const draft = this.draftValue;
    const command = this.command();
    if (!draft || !command) return undefined;
    const cells = draft.kind === 'belt' ? draft.path : footprint(draft.origin, this.sim.data.machines.get(draft.machine)?.size ?? [1, 1], draft.rotation);
    const tool = this.toolValue;
    const changed = !(tool?.kind === 'move' && draft.kind === 'machine' && keyOf(tool.from) === keyOf(draft.origin) && this.machineAt(tool.from)?.rotation === draft.rotation);
    if (!changed) return { draft, command, cells, fits: true, changed };
    const result = plan(this.sim.world, command);
    return result.ok ? { draft, command, cells, fits: true, changed } : { draft, command, cells, fits: false, reason: result.reason, changed };
  }

  /** «Confirmar»: builds the ghost. The tool stays chosen to build the next one, except after a move. */
  confirm(): Outcome {
    const ghost = this.ghost();
    if (!ghost) return { ok: false, reason: 'No hay nada que confirmar.' };
    if (!ghost.changed) return { ok: false, reason: 'Lleva la máquina a otro sitio antes de confirmar.' };
    const outcome = this.sim.execute(ghost.command);
    if (!outcome.ok) return outcome;
    const draft = ghost.draft;
    if (draft.kind === 'machine') this.machineRotation = draft.rotation;
    else this.beltSide = draft.exit;
    this.draftValue = undefined;
    if (this.toolValue?.kind === 'move') this.toolValue = undefined;
    return outcome;
  }

  /** «Cancelar»: drops the ghost; with no ghost, puts the tool down. */
  cancel(): void {
    if (this.draftValue && this.toolValue?.kind !== 'move') this.draftValue = undefined;
    else this.selectTool(undefined);
  }

  /** «Quitar» on a cell: removes what is there at once (undo brings it back). */
  remove(cell: Cell): Outcome {
    return this.sim.execute({ type: 'remove', tile: this.tile, cell });
  }

  /** The machine covering `cell`, if any. */
  machineAt(cell: Cell): MachineState | undefined {
    const interior = this.sim.world.tiles.get(this.tile)?.interior;
    return interior ? occupancy(interior).get(keyOf(cell)) : undefined;
  }

  private command(): Command | undefined {
    const draft = this.draftValue;
    const tool = this.toolValue;
    if (!draft) return undefined;
    if (draft.kind === 'belt') return { type: 'placeBelt', tile: this.tile, path: draft.path, exit: draft.exit };
    if (tool?.kind === 'move') return { type: 'moveMachine', tile: this.tile, cell: tool.from, to: draft.origin, rotation: draft.rotation };
    return { type: 'placeMachine', tile: this.tile, machine: draft.machine, cell: draft.origin, rotation: draft.rotation };
  }
}
